import { computed, watch, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { CustomField, RequestContent, useRequestContentStore } from '@/store/useRequestContentStore';
import { useViewerItemStore } from '../store/useViewerItemStore';
import { useUser } from './useUser';
import { useAiMetadataStore } from '../store/useAiMetadataStore';
import { useModal } from '@/storybook/modal/useModal';
import ParamsModal from './../components/request_content/ParamsModal.vue';

export const REQUEST_PARAMS_TO_DISPLAY_COUNT = 3;

export const useRequestContentInfoPage = () => {
  const viewerStore = useViewerItemStore();
  const store = useRequestContentStore();
  const { isCurrentItemOwner } = useUser();

  const aiMetadataStore = useAiMetadataStore();
  const { hasChapters } = storeToRefs(aiMetadataStore);
  const { requestContentId, description, requestContentCustomFieldResponses } = storeToRefs(viewerStore);
  const { list } = storeToRefs(store);
  const requestContentItem = ref<RequestContent | undefined>(undefined);

  const showHorizontalDivider = computed(() => {
    if (isCurrentItemOwner.value) {
      return true;
    }

    if (!description.value?.length || !hasChapters.value) {
      return false;
    }

    return true;
  });

  watch(list, () => (requestContentItem.value = list?.value?.find(item => item.id === requestContentId?.value)));

  const loadData = () => requestContentId?.value && store.loadById(requestContentId?.value);

  const displayValue = (response: { type: string; value: string | boolean }, definition?: CustomField) => {
    if (response.type === 'checkbox') {
      return response.value ? 'Yes' : 'No';
    }
    const option = definition?.dropdown?.options.find(item => item.value === response.value);
    return String(option?.label ?? response.value ?? '');
  };

  // Labels come from the request's field definitions, falling back to the key.
  // A ticket ID equal to the request's own Custom ID is already shown, so it is skipped.
  const allParams = computed(() => {
    const definitions = requestContentItem.value?.customFields || [];

    return requestContentCustomFieldResponses.value
      .filter(response => response.source !== 'system' || response.value !== requestContentItem.value?.customId)
      .map(response => {
        const definition = definitions.find(field => field.key === response.key);
        const label = response.source === 'system' ? 'Ticket ID' : definition?.label.custom || response.key;
        return { key: label, value: displayValue(response, definition) };
      });
  });

  const requestParamsToDisplay = computed(() => allParams.value.slice(0, REQUEST_PARAMS_TO_DISPLAY_COUNT));

  const showRequestContentParamsModal = () => {
    const { open } = useModal(ParamsModal, { modalProps: { params: allParams.value } });
    return open();
  };

  const requestContentParamsCount = computed(() => allParams.value.length);

  return {
    loadData,
    showRequestContentParamsModal,
    list,
    requestContentItem,
    showHorizontalDivider,
    requestContentId,
    requestContentParamsCount,
    requestParamsToDisplay,
  };
};
